<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceGenerationInvoices',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const panel = ref(null);
  const grid = ref(null);

  const $refs = {
    get panel() {
      return panel.value;
    },
    get grid() {
      return grid.value;
    },
  };

  const processMessage = (...args) => {
    const msg = args[0];
    if (msg.event === 'InvoiceGeneratorStatus') {
      this.$refs.panel.updateStatus(msg.data);
    }
    if (msg.event === 'InvoiceCreated') {
      this.$refs.grid.dataView.addItem(msg.data);
    }
    if (msg.event === 'InvoiceUpdated') {
      this.$refs.grid.dataView.updateItem(msg.data.id, msg.data);
    }
    if (msg.event === 'InvoiceDeleted') {
      this.$refs.grid.dataView.removeItem(msg.data.id);
    }
  };
  const onIssueInvoicesPayload = (...args) => {
    return $imodal.value
      .show({ name: 'CompleteInvoicesAlt' })
      .then((result) => {
        if (!result || result.cancel || !result.value) {
          return;
        }
        return selectedItems.value.map((p) => ({
          ...result.value,
          invoice_id: p.id,
        }));
      });
  };
  const getDownloadViewsPayload = (...args) => {
    return {
      identities: _.chain(selectedItems.value)
        .map((p) => p.id)
        .uniq()
        .value(),
    };
  };

  const gridName = ref('draftInvoices');
  const selectedItems = ref([]);
  const ts = ref(null);
  const ds = ref(null);
  const ux = ref([]);
  const b_3943151158818434_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
  }));
  const b_3943151158818434_columns = computed(() => [
    {
      field: 'invoice',
      name: $fn.value.pgettext('BillingList', 'Invoice'),
      sortPath: 'invoice.invoice_number',
      type: 'command',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <a href="#" onclick="return false;">\n\
      <strong title="{{ $value.invoice_number }}">{{ $value.invoice_number }}</strong>\n\
    </a>\n\
  {{~/if }}\n\
</span>\n\
',
          },
        ],
      },
    },
    {
      field: 'company',
      name: $fn.value.pgettext('BillingList', 'Company'),
      width: '100px',
      sortPath: 'company.code',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong title="{{ $value.name }}">{{ $value.code }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'company.id',
            },
          },
        ],
      },
    },
    {
      field: 'partner',
      name: $fn.value.pgettext('BillingList', 'Partner'),
      width: '100px',
      sortPath: 'partner.code',
      formatter: {
        name: 'chain',
        formatters: [
          {
            name: 'template',
            engine: 'hb',
            format:
              '<span>\n\
  {{~#if $value }}\n\
    <strong title="{{ $value.name }}">{{ $value.code }}</strong>\n\
  {{~/if }}\n\
</span>\n\
',
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'partner.id',
            },
          },
        ],
      },
    },
    {
      field: 'rentals',
      name: $fn.value.pgettext('InvoicesView', 'Rentals'),
      sortPath: 'rentals[0].name',
      formatter: {
        name: 'chain',
        separator: ', ',
        formatterContext: 'value',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'name',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'rental',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
    {
      field: 'contracts',
      name: $fn.value.pgettext('InvoicesView', 'Contracts'),
      sortPath: 'contracts[0].contract_number',
      formatter: {
        name: 'chain',
        separator: ', ',
        formatterContext: 'value',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'contract_number',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'contract',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
    {
      name: $fn.value.pgettext('Invoices', 'Period start'),
      field: 'billing_period.start',
      type: 'date',
    },
    {
      name: $fn.value.pgettext('Invoices', 'Period end'),
      field: 'billing_period.end',
      type: 'date',
    },
    {
      field: 'billing_category',
      name: $fn.value.pgettext('InvoiceBrowserView', 'Billing category'),
    },
    {
      field: 'net_amount',
      name: $fn.value.pgettext('InvoiceBrowserView', 'Net'),
      type: 'number',
      formatter: {
        name: 'number',
      },
    },
    {
      field: 'vat_amount',
      name: $fn.value.pgettext('InvoiceBrowserView', 'VAT'),
      type: 'number',
      formatter: {
        name: 'number',
      },
    },
    {
      field: 'amount',
      name: $fn.value.pgettext('InvoiceBrowserView', 'Amount'),
      type: 'number',
      formatter: {
        name: 'number',
      },
    },
    {
      field: 'currency',
      name: $fn.value.pgettext('InvoiceBrowserView', 'Currency'),
    },
    {
      field: 'invoice_date',
      name: $fn.value.pgettext('InvoiceBrowserView', 'Invoice date'),
      type: 'date',
    },
    {
      field: 'fulfillment_date',
      name: $fn.value.pgettext('InvoiceBrowserView', 'Fulfillment date'),
      type: 'date',
    },
    {
      field: 'due_date',
      name: $fn.value.pgettext('InvoiceBrowserView', 'Due date'),
      type: 'date',
    },
    {
      field: 'accounting_date',
      name: $fn.value.pgettext('InvoiceBrowserView', 'Accounting date'),
      type: 'date',
    },
    {
      field: 'created_at',
      name: $fn.value.pgettext('BrowserColumn', 'Create time'),
      formatter: 'datetime',
    },
  ]);

  const $data = computed(() => ({
    gridName: gridName.value,
    selectedItems: selectedItems.value,
    ts: ts.value,
    ds: ds.value,
    ux: ux.value,
    b_3943151158818434_columnDefaults: b_3943151158818434_columnDefaults.value,
    b_3943151158818434_columns: b_3943151158818434_columns.value,
  }));

  const $$load_ds = () => {
    return $fn.value.gridDataSource('list_draft_invoices');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'gridName' },
      { name: 'selectedItems' },
      { name: 'ts' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_3943151158818434_columnDefaults', nodeId: '3943151158818434' },
      { name: 'b_3943151158818434_columns', nodeId: '3943151158818434' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      gridName,
      selectedItems,
      ts,
      ds,
      ux,
      b_3943151158818434_columnDefaults,
      b_3943151158818434_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const act_3943151158818434_setData_nhn5d1ezw_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_3943151158818434_showModal_9heprcwc9_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'Invoice',
      props: {
        oid: $event.data.data.id,
        modal: true,
      },
    };
  };
  const inst_evh_3943151158818434_command_gf3zw5xus_getWhen = async (
    $event
  ) => {
    return $event.data.column.id === 'invoice';
  };
  const act_566991778050529_reloadGrid_ayvwex6vg_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const inst_evh_566991778050529_close_mbl5ozsuz_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_566991778050529_showModal_6bwoukal4_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'BatchInvoiceEditor',
      props: {
        invoices: selectedItems.value,
      },
    };
  };
  const act_3896679208055147_reloadGrid_j6sos9m3y_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const act_3896679208055147_request_5gr40pozm_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'merge_invoices',
      data: {
        items: _.chain(selectedItems.value)
          .filter((p) => _.get(p, 'id') != null)
          .map((p) => p.id)
          .uniq()
          .value(),
      },
    };
  };
  const act_1267750354288436_reloadGrid_boaahjlbb_getActionArgs = async (
    $event
  ) => {
    return {
      grid: gridName.value,
    };
  };
  const act_1267750354288436_request_rz7kdlgxr_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_draft_invoices',
      data: {
        invoices: selectedItems.value.map((p) => p.id),
      },
    };
  };

  const act_7315092382398562_reloadComponentData_0lr2042zn =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_1ausd2u03 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_0lr2042zn,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_1ausd2u03],
    event: 'reload',
  });
  const act_3943151158818434_setData_nhn5d1ezw = new actionTypes.SetDataAction({
    actionArgs: act_3943151158818434_setData_nhn5d1ezw_getActionArgs,
  });
  const inst_evh_3943151158818434_selectedRowsChanged_x8g3210h5 =
    new actionTypes.EventHandlerImpl({
      action: act_3943151158818434_setData_nhn5d1ezw,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_3943151158818434_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3943151158818434_selectedRowsChanged_x8g3210h5],
      event: 'selectedRowsChanged',
    });
  const act_3943151158818434_showModal_9heprcwc9 =
    new actionTypes.ShowModalAction({
      actionArgs: act_3943151158818434_showModal_9heprcwc9_getActionArgs,
    });
  const inst_evh_3943151158818434_command_gf3zw5xus =
    new actionTypes.EventHandlerImpl({
      action: act_3943151158818434_showModal_9heprcwc9,
      event: 'command',
      when: inst_evh_3943151158818434_command_gf3zw5xus_getWhen,
      displayName: 'showModal',
    });
  const inst_evh_3943151158818434_command = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3943151158818434_command_gf3zw5xus],
    event: 'command',
  });
  const act_566991778050529_reloadGrid_ayvwex6vg =
    new actionTypes.ReloadGridAction({
      actionArgs: act_566991778050529_reloadGrid_ayvwex6vg_getActionArgs,
    });
  const inst_evh_566991778050529_close_mbl5ozsuz =
    new actionTypes.EventHandlerImpl({
      action: act_566991778050529_reloadGrid_ayvwex6vg,
      event: 'close',
      when: inst_evh_566991778050529_close_mbl5ozsuz_getWhen,
      displayName: 'reloadGrid',
    });
  const act_566991778050529_showModal_6bwoukal4 =
    new actionTypes.ShowModalAction({
      actionArgs: act_566991778050529_showModal_6bwoukal4_getActionArgs,
      events: [inst_evh_566991778050529_close_mbl5ozsuz],
    });
  const inst_evh_566991778050529_click_wafepuuwb =
    new actionTypes.EventHandlerImpl({
      action: act_566991778050529_showModal_6bwoukal4,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_566991778050529_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_566991778050529_click_wafepuuwb],
    event: 'click',
  });
  const act_3896679208055147_reloadGrid_j6sos9m3y =
    new actionTypes.ReloadGridAction({
      actionArgs: act_3896679208055147_reloadGrid_j6sos9m3y_getActionArgs,
    });
  const inst_evh_3896679208055147_success_jupwaoa59 =
    new actionTypes.EventHandlerImpl({
      action: act_3896679208055147_reloadGrid_j6sos9m3y,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_3896679208055147_request_5gr40pozm = new actionTypes.RequestAction({
    actionArgs: act_3896679208055147_request_5gr40pozm_getActionArgs,
    displayName: 'mergeInvoices',
    events: [inst_evh_3896679208055147_success_jupwaoa59],
  });
  const inst_evh_3896679208055147_click_fx6sz0tex =
    new actionTypes.EventHandlerImpl({
      action: act_3896679208055147_request_5gr40pozm,
      event: 'click',
      displayName: 'mergeInvoices',
    });
  const inst_evh_3896679208055147_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3896679208055147_click_fx6sz0tex],
    event: 'click',
  });
  const act_1267750354288436_reloadGrid_boaahjlbb =
    new actionTypes.ReloadGridAction({
      actionArgs: act_1267750354288436_reloadGrid_boaahjlbb_getActionArgs,
    });
  const inst_evh_1267750354288436_success_4toa3fkl4 =
    new actionTypes.EventHandlerImpl({
      action: act_1267750354288436_reloadGrid_boaahjlbb,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_1267750354288436_request_rz7kdlgxr = new actionTypes.RequestAction({
    actionArgs: act_1267750354288436_request_rz7kdlgxr_getActionArgs,
    displayName: 'deleteItems',
    events: [inst_evh_1267750354288436_success_4toa3fkl4],
  });
  const inst_evh_1267750354288436_click_rbh4bvb7v =
    new actionTypes.EventHandlerImpl({
      action: act_1267750354288436_request_rz7kdlgxr,
      event: 'click',
      displayName: 'deleteItems',
    });
  const inst_evh_1267750354288436_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1267750354288436_click_rbh4bvb7v],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_reload_1ausd2u03,
    inst_evh_7315092382398562_reload,
    inst_evh_3943151158818434_selectedRowsChanged_x8g3210h5,
    inst_evh_3943151158818434_selectedRowsChanged,
    inst_evh_3943151158818434_command_gf3zw5xus,
    inst_evh_3943151158818434_command,
    inst_evh_566991778050529_close_mbl5ozsuz,
    inst_evh_566991778050529_click_wafepuuwb,
    inst_evh_566991778050529_click,
    inst_evh_3896679208055147_success_jupwaoa59,
    inst_evh_3896679208055147_click_fx6sz0tex,
    mergeInvoices: inst_evh_3896679208055147_click_fx6sz0tex,
    inst_evh_3896679208055147_click,
    inst_evh_1267750354288436_success_4toa3fkl4,
    inst_evh_1267750354288436_click_rbh4bvb7v,
    deleteItems: inst_evh_1267750354288436_click_rbh4bvb7v,
    inst_evh_1267750354288436_click,
  };

  const evh_7315092382398562_reload_1ausd2u03 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_1ausd2u03.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3943151158818434_selectedRowsChanged_x8g3210h5 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3943151158818434_selectedRowsChanged_x8g3210h5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3943151158818434_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_3943151158818434_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3943151158818434_command_gf3zw5xus = async (event, scope) => {
    return await actions.inst_evh_3943151158818434_command_gf3zw5xus.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3943151158818434_command = async (event, scope) => {
    return await actions.inst_evh_3943151158818434_command.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_566991778050529_close_mbl5ozsuz = async (event, scope) => {
    return await actions.inst_evh_566991778050529_close_mbl5ozsuz.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_566991778050529_click_wafepuuwb = async (event, scope) => {
    return await actions.inst_evh_566991778050529_click_wafepuuwb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_566991778050529_click = async (event, scope) => {
    return await actions.inst_evh_566991778050529_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3896679208055147_success_jupwaoa59 = async (event, scope) => {
    return await actions.inst_evh_3896679208055147_success_jupwaoa59.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3896679208055147_click_fx6sz0tex = async (event, scope) => {
    return await actions.inst_evh_3896679208055147_click_fx6sz0tex.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3896679208055147_click = async (event, scope) => {
    return await actions.inst_evh_3896679208055147_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1267750354288436_success_4toa3fkl4 = async (event, scope) => {
    return await actions.inst_evh_1267750354288436_success_4toa3fkl4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1267750354288436_click_rbh4bvb7v = async (event, scope) => {
    return await actions.inst_evh_1267750354288436_click_rbh4bvb7v.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1267750354288436_click = async (event, scope) => {
    return await actions.inst_evh_1267750354288436_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpBox class="h-100">
      <ItpSidebar :default-width="400" scroll-y class="me-2">
        <UcInvoiceGeneratorControls ref="panel"> </UcInvoiceGeneratorControls>
      </ItpSidebar>
      <div class="flex-fill overflow-hidden position-relative">
        <ItpDataGrid
          :name="gridName"
          :headerTitle="gettext('Draft Invoices')"
          :items="ds"
          checkbox-row-selection
          footer-totals
          export-to-csv
          :exportToCsvFilename="pgettext('ExportFilename', 'draft-invoices')"
          :columnDefaults="b_3943151158818434_columnDefaults"
          :columns="b_3943151158818434_columns"
          class="page--grid"
          ref="grid"
          @selectedRowsChanged="
            evh_3943151158818434_selectedRowsChanged($event, {})
          "
          @command="evh_3943151158818434_command($event, {})"
        >
          <template #toolbar>
            <div class="flex-grow-1 d-flex gap-2">
              <ItpButton
                :icon="$config.billingList.createItemIcon"
                :text="pgettext('Invoices', 'Bulk edit...')"
                size="sm"
                variant="light"
                :disabled="!selectedItems || !selectedItems.length"
                @click="evh_566991778050529_click($event, {})"
              >
              </ItpButton>
              <ItpButton
                :icon="$config.mergeIcon"
                :text="gettext('Merge invoices')"
                size="sm"
                variant="light"
                :disabled="!selectedItems || !selectedItems.length"
                :spinning="actions.mergeInvoices.isRunning"
                @click="evh_3896679208055147_click($event, {})"
              >
              </ItpButton>
              <ItpButton
                icon="fa-trash"
                :text="gettext('Delete')"
                size="sm"
                variant="light"
                :disabled="!selectedItems || !selectedItems.length"
                :spinning="actions.deleteItems.isRunning"
                @click="evh_1267750354288436_click($event, {})"
              >
              </ItpButton>
              <JobHandlerControl
                jobType="invoice_sync"
                load-current
                :payload="() => onIssueInvoicesPayload()"
                icon="material-symbols:cloud-sync-outline-rounded"
                :text="pgettext('DraftInvoices|Submit', 'Issue Invoices')"
                size="sm"
                :disabled="!selectedItems || !selectedItems.length"
              >
              </JobHandlerControl>
              <ItpLink
                :text="pgettext('InvoiceGeneration', 'Download invoice lines')"
                :href="$fn.urlFor('draft_invoice_lines_report')"
                button
                variant="light"
                use-auth
                :timeout="300000"
                class="d-inline-block"
              >
              </ItpLink>
              <ArtifactJob
                icon="ic:baseline-download"
                :text="gettext('Download views')"
                size="sm"
                :disabled="!selectedItems || !selectedItems.length"
                operation="download_invoice_views"
                :payload="getDownloadViewsPayload"
              >
              </ArtifactJob>
            </div>
          </template>
        </ItpDataGrid>
      </div>
    </ItpBox>
  </ItpUsercontrol>
</template>
